import { useMemo } from "react";
import { Option } from "../SelectBase";

/**
 * Custom filter function matching cmdk's signature. Returns a positive score
 * for options that should be kept.
 */
type FilterFn = (value: string, search: string) => number;

/**
 * Custom hook to filter options based on a search string.
 * Returns all options if search is empty. With a custom `filter`, keeps options
 * whose label scores positive; otherwise matches label or value by substring.
 *
 * @param options - Array of Option objects to filter.
 * @param search - Search string to filter options by.
 * @param filter - Optional custom scoring function (applied to the label).
 * @returns Filtered array of Option objects.
 */
export function useFilteredOptions(
  options: Option[],
  search: string,
  filter?: FilterFn
) {
  return useMemo(() => {
    if (!search.length) {
      return options;
    }

    if (filter) {
      return options.filter((option) => filter(option.label, search) > 0);
    }

    const searchItem = search.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchItem) ||
        option.value.toLowerCase().includes(searchItem)
    );
  }, [options, search, filter]);
}
